import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Download, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOCS = {
  brochure: {
    label: "LifeIQ Brochure",
    title: "Because marks alone won't run life.",
    description: "A short note before you read it — leave us your details and the PDF will download right after.",
    file: "LifeIQ-Brochure.pdf",
    endpoint: "/brochure",
  },
  guide: {
    label: "Complete Program Guide",
    title: "The full LifeIQ programme, in detail.",
    description: "All three levels, the cyber-safety module, classroom rhythm, and outcomes — leave us your details and the PDF will download right after.",
    file: "LifeIQ-Complete-Program-Guide.pdf",
    endpoint: "/program-guide",
  },
};

export default function BrochureDialog({ open, onOpenChange, doc = "brochure" }) {
  const meta = DOCS[doc] || DOCS.brochure;
  const [form, setForm] = useState({ name: "", email: "", phone: "", school: "", role: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const close = (v) => {
    if (!v) setForm({ name: "", email: "", phone: "", school: "", role: "" });
    onOpenChange(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const schoolField = [
        `[${meta.label}]`,
        form.school,
        form.role && `(${form.role})`,
        form.phone && `· ${form.phone}`,
      ].filter(Boolean).join(" ");
      await axios.post(`${API}/brochure-leads`, {
        name: form.name,
        email: form.email,
        school: schoolField || undefined,
      });

      const a = document.createElement("a");
      a.href = `${API}${meta.endpoint}`;
      a.download = meta.file;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();

      toast.success("Download starting. Thank you.");
      close(false);
    } catch (err) {
      toast.error("Could not start the download. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="brochure-dialog"
        className="sm:max-w-lg rounded-sm border border-[#E5E7EA] bg-white p-0 overflow-hidden"
      >
        <div className="bg-[#0D1B2A] text-white px-7 py-6">
          <div className="flex items-center gap-3 text-[12px] uppercase tracking-[0.18em] text-[#F57F17] font-semibold">
            <FileText size={16} /> {meta.label}
          </div>
          <DialogHeader className="mt-3 space-y-2">
            <DialogTitle className="font-serif-d text-[26px] sm:text-[30px] leading-[1.15] text-white">
              {meta.title}
            </DialogTitle>
            <DialogDescription className="text-white/75 text-[15px]">
              {meta.description}
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={submit} className="px-7 py-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="br-name" className="text-[12px] uppercase tracking-[0.14em] text-[#5C6470] font-semibold">Name *</Label>
              <Input id="br-name" data-testid="brochure-name" value={form.name} onChange={set("name")} placeholder="Full name" className="border-[#D7DADD] focus-visible:ring-[#F57F17]" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="br-email" className="text-[12px] uppercase tracking-[0.14em] text-[#5C6470] font-semibold">Email *</Label>
              <Input id="br-email" data-testid="brochure-email" type="email" value={form.email} onChange={set("email")} placeholder="name@school.in" className="border-[#D7DADD] focus-visible:ring-[#F57F17]" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="br-phone" className="text-[12px] uppercase tracking-[0.14em] text-[#5C6470] font-semibold">Phone</Label>
              <Input id="br-phone" data-testid="brochure-phone" value={form.phone} onChange={set("phone")} placeholder="+91" className="border-[#D7DADD] focus-visible:ring-[#F57F17]" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="br-role" className="text-[12px] uppercase tracking-[0.14em] text-[#5C6470] font-semibold">Role</Label>
              <Select value={form.role} onValueChange={set("role")}>
                <SelectTrigger id="br-role" data-testid="brochure-role" className="border-[#D7DADD] focus:ring-[#F57F17]"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="School leader">School leader</SelectItem>
                  <SelectItem value="Teacher">Teacher</SelectItem>
                  <SelectItem value="Parent">Parent</SelectItem>
                  <SelectItem value="Investor">Investor</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="br-school" className="text-[12px] uppercase tracking-[0.14em] text-[#5C6470] font-semibold">School / Organisation</Label>
              <Input id="br-school" data-testid="brochure-school" value={form.school} onChange={set("school")} placeholder="(optional)" className="border-[#D7DADD] focus-visible:ring-[#F57F17]" />
            </div>
          </div>

          <p className="text-[13px] text-[#5C6470] leading-relaxed pt-1">
            We use your details only to share the document and, occasionally, an honest update. No spam.
          </p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => close(false)}
              className="px-5 py-3 rounded-sm text-[13px] font-semibold uppercase tracking-wide border border-[#D7DADD] text-[#0D1B2A] hover:bg-[#F5F5F4] transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="brochure-submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 bg-[#F57F17] text-white px-7 py-3 rounded-sm text-[13px] font-semibold uppercase tracking-wide hover:brightness-95 transition disabled:opacity-70"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {submitting ? "Preparing…" : `Download ${doc === "guide" ? "Guide" : "Brochure"}`}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
